<script setup lang="ts">
/**
 * [#022fa07d] Горизонтальный слайдер скринов для /faq/web (шаг Configurator).
 * Слайды во всю ширину контейнера, единая высота (object-contain), scroll-snap,
 * стрелки + точки + счётчик. Без библиотек.
 */
import { ref, onMounted, onUnmounted } from 'vue';
import { ChevronLeft, ChevronRight } from 'lucide-vue-next';

const props = defineProps<{ images: string[] }>();

const track = ref<HTMLElement | null>(null);
const current = ref(0);

function children(): HTMLElement[] {
  return track.value ? (Array.from(track.value.children) as HTMLElement[]) : [];
}

function goTo(i: number) {
  const kids = children();
  const idx = Math.max(0, Math.min(kids.length - 1, i));
  track.value?.scrollTo({ left: kids[idx]?.offsetLeft ?? 0, behavior: 'smooth' });
}

function onScroll() {
  if (!track.value) return;
  const left = track.value.scrollLeft;
  const kids = children();
  let best = 0;
  let bestDist = Infinity;
  kids.forEach((k, i) => {
    const d = Math.abs(k.offsetLeft - left);
    if (d < bestDist) { bestDist = d; best = i; }
  });
  current.value = best;
}

let raf = 0;
function onScrollRaf() {
  cancelAnimationFrame(raf);
  raf = requestAnimationFrame(onScroll);
}

onMounted(() => track.value?.addEventListener('scroll', onScrollRaf, { passive: true }));
onUnmounted(() => {
  track.value?.removeEventListener('scroll', onScrollRaf);
  cancelAnimationFrame(raf);
});
</script>

<template>
  <div class="relative">
    <!-- Лента слайдов -->
    <div
      ref="track"
      class="flex overflow-x-auto snap-x snap-mandatory scroll-smooth rounded-xl border border-ink-100 bg-ink-50/60 no-scrollbar"
    >
      <div
        v-for="(src, i) in images"
        :key="src"
        class="shrink-0 snap-start p-2.5 sm:p-3 h-[280px] sm:h-[360px]"
      >
        <img
          :src="src"
          :alt="`slide ${i + 1}`"
          loading="lazy"
          class="h-full w-auto object-contain rounded-lg shadow-sm"
        />
      </div>
    </div>

    <!-- Управление под слайдером: стрелки + точки + счётчик -->
    <div class="mt-3 flex items-center justify-center gap-3">
      <button
        type="button"
        class="w-9 h-9 rounded-full bg-white border border-ink-200 shadow-sm flex items-center justify-center text-ink-700 transition hover:bg-ink-50 disabled:opacity-30 disabled:cursor-default"
        :disabled="current === 0"
        aria-label="prev"
        @click="goTo(current - 1)"
      >
        <ChevronLeft :size="18" />
      </button>
      <div class="flex items-center">
        <button
          v-for="(_, i) in images"
          :key="i"
          type="button"
          class="flex items-center justify-center h-6 min-w-6"
          :aria-label="`slide ${i + 1}`"
          @click="goTo(i)"
        >
          <span class="h-2 rounded-full transition-all" :class="i === current ? 'w-6 bg-ink-900' : 'w-2 bg-ink-300 hover:bg-ink-400'" />
        </button>
      </div>
      <span class="text-[12px] font-semibold text-ink-700 tabular-nums">{{ current + 1 }} / {{ images.length }}</span>
      <button
        type="button"
        class="w-9 h-9 rounded-full bg-white border border-ink-200 shadow-sm flex items-center justify-center text-ink-700 transition hover:bg-ink-50 disabled:opacity-30 disabled:cursor-default"
        :disabled="current === images.length - 1"
        aria-label="next"
        @click="goTo(current + 1)"
      >
        <ChevronRight :size="18" />
      </button>
    </div>
  </div>
</template>

<style scoped>
.no-scrollbar {
  scrollbar-width: none;
}
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
</style>
